import PropTypes from 'prop-types';
import React from 'react';
import { UX2 } from '@wsb/guac-widget-core';
import dataAids from '../../../constants/dataAids';
import {
  OlsConfigStore,
  RouteHandler,
  CartActions,
  ShopViewConstants,
  CartStore
} from '@wsb/guac-widget-shared/lib/common/ols-core/core-bundle';
import FormattedNumber from '../FormattedNumber';
import IconWithLabel from '../shared/IconWithLabel';
import * as Metrics from '../utils/trackMetric';
import * as MetricEvents from '../../../constants/metrics';
import { mapContextToProps } from '../OlsShopContext';

export class CartLineItem extends React.Component {
  constructor() {
    super(...arguments);
    this.handleProductDetailClick = this.handleProductDetailClick.bind(this);
    this.handleDeleteClick = this.handleDeleteClick.bind(this);
  }

  handleProductDetailClick(e) {
    e.preventDefault();
    const { lineItem } = this.props;
    const { slug } = lineItem;
    RouteHandler.navigate(ShopViewConstants.PRODUCT_DETAIL, { slug: slug });
  }

  handleDeleteClick() {
    const { lineItem } = this.props;
    const { cart } = CartStore.getState();
    const { id, product_type } = lineItem;
    const beginningCartSize = cart && cart.line_items ? cart.line_items.length : 0;

    Metrics.track(
      MetricEvents.CART_REMOVE_ITEM_CLICK({
        productType: product_type,
        beginningCartSize
      })
    );

    CartActions.removeFromCart(id);
  }

  selectedOptionsData(selectedOptions, lineItemId) {
    const { Block, Details } = UX2.Element;
    if (selectedOptions && selectedOptions.length > 0) {
      const lineItemOptions = selectedOptions.map(option => {
        const { name, value } = option;
        const key = `option_${lineItemId}_${name}`;
        const styles = {
          outer: {
            marginTop: 'xxsmall'
          },
          inner: {
            display: 'inline',
            paddingRight: 'xsmall'
          }
        };
        return (
          <Details key={ key } style={ styles.outer }>
            <Block children={ `${name}:` } style={ styles.inner } />
            <Block children={ value } style={{ display: 'inline' }} />
          </Details>
        );
      });
      return <Block children={ lineItemOptions } style={{ marginTop: 'small' }} />;
    }
    return null;
  }

  get backorderedWarning() {
    const { lineItem, translate } = this.props;
    if (
      !lineItem ||
      !lineItem.track_inventory ||
      !lineItem.hasOwnProperty('in_stock') ||
      lineItem.in_stock
    ) {
      return null;
    }

    return (
      <IconWithLabel
        containerMarginTop='xxsmall'
        icon='clock'
        iconSize='small'
        labelElement={ UX2.Element.Details.Minor }
        labelText={ translate('products.item_backordered') }
      />
    );
  }

  render() {
    const { lineItem, lastItem, quantity, error, getLinkProps } = this.props;
    const { slug } = lineItem;
    const linkProps = getLinkProps(ShopViewConstants.PRODUCT_DETAIL, { slug });
    const { id, name, price, subtotal, total, selected_options, variant_id, variant_asset_ids } =
      lineItem;
    const { Block, Link, Input, Error } = UX2.Element;
    const {
      Grid,
      Grid: { Cell }
    } = UX2.Component;
    let assets = lineItem.assets || lineItem.images;
    if (variant_id && variant_asset_ids && variant_asset_ids.length) {
      // if the lineItem's variant has assets (images, videos) associated with
      // it, we show the first one
      const index = assets.findIndex(asset => asset.id === variant_asset_ids[0]);
      if (assets && assets.length) {
        assets = assets.slice(index, index + 1);
      }
    }
    const lineItemTotal = subtotal || total;
    const selectedOptionsData = this.selectedOptionsData(selected_options, id);
    const styles = {
      lineItemOuter: {
        borderBottomWidth: lastItem ? '0' : '1px',
        borderBottomStyle: lastItem ? 'none' : 'solid',
        borderColor: 'section'
      },
      lineItemInner: {
        'paddingTop': 'small',
        'paddingBottom': lastItem ? 'medium' : 'small',
        '@sm': {
          paddingBottom: 'small'
        }
      },
      grid: {
        'alignItems': 'flex-start',
        '@sm': {
          alignItems: 'center'
        }
      },
      assetLink: {
        'display': 'inline-block',
        'maxWidth': '100%',
        '@xs-only': {
          marginTop: 'xxsmall'
        }
      },
      gridCell: {
        'marginBottom': 'xsmall',
        '@sm': {
          marginBottom: '0'
        }
      },
      nameLink: {
        display: 'inline-block'
      },
      qtyWrapper: {
        'display': 'flex',
        'alignItems': 'center',
        'justifyPosition': 'flex-end',
        '@sm': {
          justifyPosition: 'flex-start'
        }
      },
      qty: {
        'minWidth': '7rem',
        '@xs-only': {
          minWidth: '5rem'
        }
      },
      deleteWrapper: {
        'marginLeft': 'medium',
        '@sm': {
          marginLeft: 'xsmall'
        },
        'z-index': '1'
      },
      delete: {
        'fontWeight': 'bold',
        'fontColor': 'highContrast',
        '@xs-only': {
          fontSize: 'large'
        }
      },
      error: {
        marginTop: 'xxsmall'
      },
      totalCell: {
        'textAlign': 'right',
        '@xs-only': {
          display: 'none'
        }
      }
    };

    return (
      <Block style={ styles.lineItemOuter }>
        <Block style={ styles.lineItemInner }>
          <Grid size={ 12 } style={ styles.grid }>
            <Cell xs={ 3 } sm={ 1 } style={{ '@sm': { paddingRight: '0' }, 'textAlign': 'center' }}>
              <Link
                onClick={ this.handleProductDetailClick }
                href='javascript:;'
                { ...linkProps }
                style={ styles.assetLink }
              >
                <UX2.Group.Product.Asset
                  noProductImage={ OlsConfigStore.getState().noProductImage }
                  name={ name }
                  assets={ assets }
                  boxed={ false }
                />
              </Link>
            </Cell>
            <Cell xs={ 9 } sm={ 11 }>
              <Grid style={{ alignItems: 'center' }}>
                <Cell xs={ 12 } sm={ 5 } md={ 6 } style={ styles.gridCell }>
                  <Link
                    children={ name }
                    onClick={ this.handleProductDetailClick }
                    href='javascript:;'
                    { ...linkProps }
                    style={ styles.nameLink }
                    featured
                  />
                  { this.backorderedWarning }
                  { selectedOptionsData }
                </Cell>
                <Cell xs={ 5 } sm={ 2 } style={ styles.gridCell }>
                  <FormattedNumber
                    number={ +price }
                    currency={ true }
                    data-aid={ dataAids.CART_LINE_ITEM_PRICE }
                  />
                </Cell>
                <Cell xs={ 7 } sm={ 3 } md={ 2 } style={ styles.gridCell }>
                  <Block style={ styles.qtyWrapper }>
                    <Input
                      tag='input'
                      type='number'
                      pattern='[0-9]*'
                      min='1'
                      max='999'
                      placeholder={ this.props.placeholder }
                      data-aid={ dataAids.CART_ITEM_QUANTITY_RENDERED }
                      value={ +quantity }
                      onChange={ this.props.onChange.bind(this, id) }
                      style={ styles.qty }
                    />
                    <Block style={ styles.deleteWrapper }>
                      <Link
                        onClick={ this.handleDeleteClick }
                        href='javascript:;'
                        data-aid={ dataAids.CART_REMOVE_LINK }
                        children='✕'
                        style={ styles.delete }
                      />
                    </Block>
                  </Block>
                  { error && <Error children={ error } style={ styles.error } /> }
                </Cell>
                <Cell sm={ 2 } style={ styles.totalCell }>
                  <FormattedNumber
                    data-aid={ dataAids.CART_LINE_ITEM_TOTAL }
                    number={ +lineItemTotal }
                    currency={ true }
                    style={{
                      fontColor: 'highContrast'
                    }}
                  />
                </Cell>
              </Grid>
            </Cell>
          </Grid>
        </Block>
      </Block>
    );
  }
}

CartLineItem.defaultProps = {
  onChange: function () {},
  translate: () => {}
};

CartLineItem.propTypes = {
  lineItem: PropTypes.object.isRequired,
  lastItem: PropTypes.bool,
  quantity: PropTypes.number.isRequired,
  onChange: PropTypes.func,
  error: PropTypes.string,
  placeholder: PropTypes.any,
  getLinkProps: PropTypes.func,
  translate: PropTypes.func
};

export default mapContextToProps(CartLineItem, context => ({
  getLinkProps: context.getLinkProps,
  translate: context.translate
}));
